import { formatHourMinute } from 'src/components/dispatch/shiftTruckRowInfo';
import { DRIVER_UNSET_LABEL, TRUCK_UNSET_LABEL } from 'src/constants/assignmentLabels';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { VehicleInfoExtra } from 'src/pages/ConvenienceDispatches/adapter';

// convenience Presenter.tsx の vehicleExtraMap(715-806)/makeExtra(742-771) を、
// P0 (固定ルート未使用) の前提で「delivery 由来の全 truck を網羅する末尾ループ」だけに簡約したもの。
// フィールド構成・変換係数・temporary(未設定)判定は makeExtra と完全一致させる。
export function buildVehicleExtraMap(
  deliveries: PlanningsDeliveryWithLegRunEntity[],
  trucks: PlanningsTruckEntity[],
  drivers: PlanningsDriverEntity[],
): Map<number, VehicleInfoExtra> {
  const truckMasterMap = new Map(trucks.map((t) => [t.id, t]));
  const driverMasterMap = new Map(drivers.map((d) => [d.id, d]));

  const map = new Map<number, VehicleInfoExtra>();
  for (const d of deliveries) {
    // truckId ごとに先頭の delivery のみを採用する (makeExtra の truckDriverMap/
    // shiftTruckByTruckId 等と同じく「最初に見つかった値」を代表値として使う規則)
    if (map.has(d.truckId)) continue;

    const t = truckMasterMap.get(d.truckId);
    const drv = driverMasterMap.get(d.driverId);
    // PlanningsTruckEntity は 重量=g / 体積=mm3 のみ保持するため単位変換する。
    // 0 は「未設定」として null 扱いする (makeExtra の maxG > 0 判定と同一)
    const maxG = t?.maximumLoadingCapacityWeightForCalculation ?? 0;
    const maxMm3 = t?.loadingPlatformVolumeMm3 ?? 0;
    // temporary truck は「車両未設定」表示 (V3Plans/V3Timeline と同様の規則)
    const truckName = t?.temporary ? TRUCK_UNSET_LABEL : t?.licensePlateValue ?? null;

    map.set(d.truckId, {
      partnerCompanyName: t?.temporary ? null : t?.partnerCompanyName ?? null,
      truckName,
      // 車番の優先順位: shift_truck.truck_number (= delivery.truckNumber) が最優先
      shiftTruckNumber: d.truckNumber ?? null,
      masterTruckNumber: t?.temporary ? null : t?.truckNumber ?? null,
      truckLabel: truckName,
      defaultDriverName: drv?.temporary ? DRIVER_UNSET_LABEL : drv?.name ?? null,
      shiftStart: formatHourMinute(d.startAt),
      shiftEnd: formatHourMinute(d.endAt),
      workingAvailableSeconds: d.workingAvailableDurationSeconds ?? null,
      driverId: d.driverId ?? null,
      shiftTruckId: d.id,
      capacityCases: null,
      capacityQuantity: d.maximumLoadingCapacityQuantity ?? null,
      capacityKg: maxG > 0 ? maxG / 1000 : null,
      capacityM3: maxMm3 > 0 ? maxMm3 / 1_000_000_000 : null,
      // 3 つ目の色経路。表示色は truckColorMap.ts の buildTruckColorMap
      // (最初の**非 null**) が正本で、ここはループ先頭の `map.has(d.truckId)` ガードに
      // より同一 truckId の**先頭** delivery で確定する。先頭が null なら後続の非 null を
      // 取りこぼすので、正本と食い違う。Vehicle.color の読み手が現状 0 件（grep 済み）
      // なので実害は無いが、描き始めるなら buildTruckColorMap へ寄せること。
      truckColor: d.truckColor ?? null,
      partnerCompanyId: t?.partnerCompanyId ?? null,
      partnerCompanyColor: t?.temporary ? null : t?.partnerCompanyColor ?? null,
      truckKlass: t?.klass ?? null,
    });
  }
  return map;
}
